import type { BuildContext } from "../types.js";
import { scaleFor } from "../format.js";
import { backgroundGradient } from "../primitives/backgroundGradient.js";
import { brandLogo } from "../primitives/brandLogo.js";
import { hookText } from "../primitives/hookText.js";
import { cta } from "../primitives/cta.js";
import { card } from "../primitives/card.js";
import { safeArea } from "../primitives/safeArea.js";
import { getLocale, type LocaleCode } from "../../locales/index.js";
import {
  problemSolutionSchema,
  type ProblemSolutionProps,
} from "../../templates/index.js";
import { escapeHtml } from "../composition.js";
import { assemble } from "./shared.js";

export const PROBLEM_SOLUTION_DURATION_SEC = 9;

export function buildProblemSolutionHtml(
  rawProps: unknown,
  ctx: Omit<BuildContext, "durationSec">,
): string {
  const props = problemSolutionSchema.parse(rawProps) as ProblemSolutionProps;
  const loc = getLocale(ctx.locale as LocaleCode);
  const ctaText = props.cta || loc.ctas[props.ctaKey as keyof typeof loc.ctas];

  const fullCtx: BuildContext = { ...ctx, durationSec: PROBLEM_SOLUTION_DURATION_SEC };

  const titleSize = scaleFor(ctx.format, { base: 76, short: 86, square: 70, wide: 80 });
  const labelSize = scaleFor(ctx.format, { base: 36, short: 40, square: 34, wide: 38 });
  const ctaSize = scaleFor(ctx.format, { base: 50, short: 60, square: 46, wide: 54 });
  const logoSize = scaleFor(ctx.format, { base: 80, short: 100, square: 80, wide: 90 });

  const bg = backgroundGradient(fullCtx, "bg", { variant: "dark", animated: true });

  const probStart = 4 / 30;
  const probDur = 120 / 30;
  const solStart = 70 / 30;
  const solDur = 150 / 30;
  const ctaStart = 170 / 30;
  const ctaDur = 60 / 30;

  const logoFrag = brandLogo({ id: "tpl-logo", size: logoSize, color: "white" });
  const ctaFrag = cta({ id: "tpl-cta", text: ctaText, startSec: ctaStart, durationSec: ctaDur, size: ctaSize });

  const problemHook = hookText({
    id: "tpl-prob-title",
    text: props.problem,
    startSec: probStart,
    size: titleSize,
    color: "#fff",
    align: "left",
  });
  const solutionHook = hookText({
    id: "tpl-sol-title",
    text: props.solution,
    startSec: solStart,
    size: titleSize,
    color: "#fff",
    align: "left",
  });

  const labelStyle = (color: string) =>
    `font-weight:700;font-size:${labelSize}px;color:${color};text-transform:uppercase;letter-spacing:2px;margin-bottom:16px;`;

  const problemCardInner = `<div style="${labelStyle("#fca5a5")}">${escapeHtml(loc.labels.problem)}</div>${problemHook.html}`;
  const solutionCardInner = `<div style="${labelStyle("#86efac")}">${escapeHtml(loc.labels.solution)}</div>${solutionHook.html}`;

  const problemCardFrag = card({
    id: "tpl-prob-card",
    childrenHtml: problemCardInner,
    startSec: probStart,
    variant: "filled",
  });
  const solutionCardFrag = card({
    id: "tpl-sol-card",
    childrenHtml: solutionCardInner,
    startSec: solStart,
    variant: "outline",
  });

  const innerHtml = `
<div style="display:flex;justify-content:flex-start;">${logoFrag.html}</div>
<div id="tpl-stack" style="flex:1;display:flex;flex-direction:column;justify-content:center;gap:36px;">
  <div id="tpl-prob-clip" class="clip" data-start="${probStart}" data-duration="${probDur}" data-track-index="0">${problemCardFrag.html}</div>
  <div id="tpl-sol-clip" class="clip" data-start="${solStart}" data-duration="${solDur}" data-track-index="1">${solutionCardFrag.html}</div>
</div>
<div id="tpl-cta-clip" class="clip" data-start="${ctaStart}" data-duration="${ctaDur}" data-track-index="2" style="display:flex;justify-content:center;">${ctaFrag.html}</div>
`;

  const safe = safeArea({ id: "tpl-safe", format: ctx.format, childrenHtml: innerHtml });

  return assemble({
    templateId: "ProblemSolution",
    ctx: fullCtx,
    background: bg,
    contentFragments: [
      logoFrag,
      problemHook,
      solutionHook,
      problemCardFrag,
      solutionCardFrag,
      ctaFrag,
    ],
    safeAreaInnerHtml: innerHtml,
    safeArea: safe,
  });
}
